import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

// Both apps own their UI and mutations. The theme owns cart rendering/counts.
// In particular, Mule's replaceChildren hot reload must not own this drawer.
const drawer = document.querySelector('theme-drawer#cart-drawer');

if (drawer) {
  const relayEvents = new WeakSet();
  let pendingNativeUpdates = 0;
  let revision = 0;
  let refreshRequested = false;
  let refreshTimer;
  let cartRead;
  let redrawSlot;
  let redrawTimeout;
  let upsellSnapshot;
  let upsellHold;
  let upsellHoldTimeout;
  let upsellHoldFrame;
  function reconcileProtectionRender(event) {
    const detail = event.detail;
    if (detail?.skipWidgetRender !== true || typeof detail.widgetState?.checked !== 'boolean') return;
    const checkbox = drawer.querySelector('[data-cart-protection-slot] input[type="checkbox"]');
    // An async SDK handler can finish after selector callbacks replaced its root.
    // Its skip-render optimisation then leaves the replacement at old consent.
    // Let the SDK consume its own completed state; don't set checked, click the
    // control, fabricate an event, or write consent/cart data ourselves.
    if (checkbox && checkbox.checked !== detail.widgetState.checked) detail.skipWidgetRender = false;
  }

  // Capture precedes the SDK's state listener, including an already-loaded app.
  document.addEventListener('MuleCartUpdate', reconcileProtectionRender, true);

  function resetProtectionToggle() {
    const checkbox = drawer.querySelector('[data-cart-protection-slot] input[type="checkbox"]');
    // Let the SDK persist consent and remove its own charge; never edit its storage.
    if (checkbox?.checked && !checkbox.disabled) checkbox.click();
  }

  function protectionRows() {
    return [...drawer.querySelectorAll('tr[data-key]')];
  }

  function merchandiseItemCount(cart) {
    if (!Array.isArray(cart?.items)) return cart?.item_count ?? 0;
    return cart.items.reduce(
      (count, item) => count + (item.handle === 'protectmyorder' ? 0 : Number(item.quantity) || 0),
      0
    );
  }

  // SellEasy removes its custom element before remounting offers. Keep a visual,
  // inert copy through that gap; never reconnect or duplicate provider elements.
  function copyUpsellVisual(node) {
    if (node.nodeType === Node.TEXT_NODE) return document.createTextNode(node.textContent);
    if (node.nodeType !== Node.ELEMENT_NODE) return document.createDocumentFragment();
    if (node.namespaceURI === 'http://www.w3.org/2000/svg') return node.cloneNode(true);

    const tag = node.tagName.toLowerCase();
    if (['script', 'style', 'iframe', 'object', 'embed', 'link', 'meta'].includes(tag)) {
      return document.createDocumentFragment();
    }
    if (tag === 'select' || tag === 'input') {
      const copy = node.cloneNode(true);
      for (const element of [copy, ...copy.querySelectorAll('*')]) {
        for (const { name } of [...element.attributes]) {
          if (name === 'id' || name === 'name' || name.startsWith('on')) element.removeAttribute(name);
        }
      }
      return copy;
    }

    const custom = tag.includes('-');
    const control = ['button', 'a', 'textarea', 'form'].includes(tag);
    const display = custom || control ? getComputedStyle(node).display : '';
    const copy = document.createElement(custom || control ? (display === 'inline' ? 'span' : 'div') : tag);
    for (const { name, value } of node.attributes) {
      if (['style', 'class', 'src', 'srcset', 'sizes', 'alt', 'width', 'height'].includes(name)) {
        copy.setAttribute(name, value);
      }
    }
    if (custom) copy.style.display = display;
    for (const child of node.childNodes) copy.appendChild(copyUpsellVisual(child));
    return copy;
  }

  function captureUpsellSnapshot() {
    if (upsellHold) return;
    const widget = drawer.querySelector('[data-cart-upsell-slot] lb-upsell-widget');
    if (widget?.querySelector('lb-upsell-flat-card')) upsellSnapshot = copyUpsellVisual(widget);
  }

  function clearUpsellHold() {
    clearTimeout(upsellHoldTimeout);
    cancelAnimationFrame(upsellHoldFrame);
    upsellHold?.remove();
    upsellHold = undefined;
    upsellSnapshot = undefined;
  }

  function showUpsellHold(slot) {
    if (!upsellSnapshot || upsellHold || !slot.isConnected) return;
    upsellHold = document.createElement('div');
    upsellHold.className = 'lazy-oaf-cart-drawer__upsell-hold';
    upsellHold.setAttribute('aria-hidden', 'true');
    upsellHold.inert = true;
    upsellHold.appendChild(upsellSnapshot);
    slot.appendChild(upsellHold);
    upsellSnapshot = undefined;
    // A failed or ineligible redraw must not leave stale offers on screen.
    upsellHoldTimeout = setTimeout(clearUpsellHold, 2000);
  }

  function settleUpsellHold() {
    if (!upsellHold) return;
    if (!upsellHold.isConnected) return clearUpsellHold();
    const widget = drawer.querySelector('[data-cart-upsell-slot] lb-upsell-widget');
    if (!widget?.querySelector('lb-upsell-flat-card') || upsellHoldFrame) return;
    upsellHoldFrame = requestAnimationFrame(() => {
      upsellHoldFrame = undefined;
      if (widget.isConnected && widget.querySelector('lb-upsell-flat-card')) clearUpsellHold();
    });
  }

  function releaseUpsellGeometry() {
    clearTimeout(redrawTimeout);
    if (redrawSlot) {
      redrawSlot.style.minHeight = '';
      redrawSlot.style.display = '';
    }
    redrawSlot = undefined;
  }

  function reserveUpsellGeometry(event) {
    if (event?.detail?.cart?.item_count === 0) {
      clearUpsellHold();
      releaseUpsellGeometry();
      return;
    }
    const slot = drawer.querySelector('[data-cart-upsell-slot]');
    if (!slot) return;
    const height = slot.getBoundingClientRect().height;
    if (height <= 0) return;
    releaseUpsellGeometry();
    redrawSlot = slot;
    slot.style.minHeight = `${height}px`;
    slot.style.display = 'block'; // Override :empty only during this redraw.
    // SellEasy may remove/recreate its eligible widget while fetching offers.
    // Release on its rendered event; never retain a gap after a failed redraw.
    redrawTimeout = setTimeout(releaseUpsellGeometry, 2000);
  }

  function mountUpsells() {
    const rows = protectionRows();
    if (rows.length > 0 && rows.every((row) => row.dataset.cartProtectionLine !== undefined)) {
      // Covers an existing orphan basket when the provider first mounts.
      resetProtectionToggle();
    }
    const slot = drawer.querySelector('[data-cart-upsell-slot]');
    if (!slot) return; // Empty carts intentionally remove the whole app region.
    const widgets = drawer.querySelectorAll(
      'lb-upsell-widget[widget="widget#icuc"], lb-upsell-widget[widget="widget#icul"]'
    );
    for (const widget of widgets) {
      // Move the actual custom element, not a clone of provider-owned markup.
      // SellEasy initially appends it to #cart-form, outside the preserved slot.
      if (widget.parentElement !== slot) slot.appendChild(widget);
    }
  }

  mountUpsells();
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      if (record.target !== drawer.querySelector('[data-cart-upsell-slot]')) continue;
      if ([...record.removedNodes].some((node) => node.nodeType === Node.ELEMENT_NODE && node.matches('lb-upsell-widget'))) {
        showUpsellHold(record.target);
      }
    }
    mountUpsells();
    settleUpsellHold();
  });
  observer.observe(drawer, { childList: true, subtree: true });

  function invalidateRead() {
    revision++;
    clearTimeout(refreshTimer);
    if (cartRead) {
      refreshRequested = true;
      cartRead.abort();
      cartRead = undefined;
    }
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    if (refreshRequested && pendingNativeUpdates === 0) {
      // Mule and SellEasy can report the same mutation at adjacent boundaries.
      refreshTimer = setTimeout(refreshCart, 80);
    }
  }

  function onProviderUpdate() {
    invalidateRead();
    refreshRequested = true;
    scheduleRefresh();
  }

  function onNativeUpdate(event) {
    if (relayEvents.has(event) || !event.promise) return;
    captureUpsellSnapshot();
    const rows = protectionRows();
    const removed = new Set(event.lines?.filter((line) => line.quantity === 0).map((line) => line.id));
    if (rows.some((row) => row.dataset.cartProtectionLine !== undefined && removed.has(row.dataset.key))) {
      resetProtectionToggle();
    }
    reserveUpsellGeometry();
    pendingNativeUpdates++;
    invalidateRead();
    const settled = () => {
      pendingNativeUpdates--;
      scheduleRefresh();
    };
    event.promise.then(settled, settled);
  }

  async function refreshCart() {
    if (!refreshRequested || pendingNativeUpdates > 0) return;
    refreshRequested = false;
    const currentRevision = revision;
    const controller = new AbortController();
    cartRead = controller;
    const result = CartLinesUpdateEvent.createPromise();
    const event = new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [],
      promise: result.promise,
    });
    relayEvents.add(event);
    document.dispatchEvent(event);

    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        signal: controller.signal,
      });
      if (controller.signal.aborted || currentRevision !== revision) {
        const error = new Error('Provider cart refresh superseded');
        error.name = 'AbortError';
        throw error;
      }
      if (!response?.ok) throw new Error(`Provider cart refresh failed: ${response?.status ?? 'no response'}`);
      const cart = await response.json();
      if (controller.signal.aborted || currentRevision !== revision) {
        const error = new Error('Provider cart refresh superseded');
        error.name = 'AbortError';
        throw error;
      }
      result.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { itemCount: merchandiseItemCount(cart), source: 'lazy-oaf-cart-providers' },
      });
    } catch (error) {
      // Native consumers retain their existing rejection/canonical recovery path.
      result.reject(error);
    } finally {
      if (cartRead === controller) cartRead = undefined;
      scheduleRefresh();
    }
  }

  document.addEventListener(StandardEvents.cartLinesUpdate, onNativeUpdate);
  document.addEventListener('MuleCartUpdate', captureUpsellSnapshot, true);
  document.addEventListener('MuleCartUpdate', () => {
    reserveUpsellGeometry();
    onProviderUpdate();
  });
  // Start early for theme/protection mutations; this also covers SDK-only changes.
  window.addEventListener('lbSECartUpdated', captureUpsellSnapshot, true);
  window.addEventListener('lbSECartUpdated', reserveUpsellGeometry, true);
  window.addEventListener('lbWidgetRendered', (event) => {
    if (event.detail?.widgetSrcElement?.parentElement === redrawSlot) releaseUpsellGeometry();
    settleUpsellHold();
  });
  window.addEventListener('lbSECartUpdated', onProviderUpdate);
}
